// Overview (plan step 2.4; D58): Apsio's funnel without opening PostHog. Sign-ups, then the
// organizations whose trial started in the window, how many received a first session and how
// many chose a paid plan, trial conversion, weekly active organizations and Free. Counts of
// Apsio's own business events only; the API reads PostHog at most once a minute. And Free's
// global cost ceiling (D107): staff pause or resume new organizations that would start on Free.
// Console D's sections (APS-412 G1): two strips of four, PostHog not set up as one dim line, and
// the pause in a form window inside its section.
import { PageHead, Section, Strip } from '@apsio/ui'
import { useQuery } from '@tanstack/react-query'
import {
  funnelQuery,
  platformControlsQuery,
  setFreeOrgsPaused,
  useAdminMutation,
} from '../api/queries'
import { ActionForm } from '../components/ActionForm'
import { ErrorState, Loading } from '../components/states'
import { useI18n } from '../i18n'
import { percent as formatPercent } from '../lib/format'
import { PostHogSource } from './posthog'

export function OverviewPage() {
  const { t, number } = useI18n()
  const funnel = useQuery(funnelQuery)
  const percent = (n: number | null) => (n === null ? t('common.none') : formatPercent(number, n))
  return (
    <main id="main" className="pg">
      <PageHead title={t('overview.title')} meta={t('overview.lede')} />
      {funnel.isPending ? (
        <Loading />
      ) : funnel.isError ? (
        <ErrorState error={funnel.error} retry={() => funnel.refetch()} />
      ) : (
        <>
          <Section
            title={t('overview.funnelTitle')}
            meta={funnel.data.data ? t('common.days', { count: funnel.data.data.days }) : undefined}
          >
            <PostHogSource section={funnel.data}>
              {funnel.data.data ? (
                <Strip
                  items={[
                    { dt: t('overview.trials'), dd: number(funnel.data.data.trials) },
                    {
                      dt: t('overview.firstSession'),
                      dd: number(funnel.data.data.with_first_session),
                    },
                    { dt: t('overview.paid'), dd: number(funnel.data.data.paid) },
                    {
                      dt: t('overview.conversion'),
                      dd: percent(funnel.data.data.trial_conversion),
                    },
                  ]}
                />
              ) : null}
            </PostHogSource>
          </Section>
          {funnel.data.data ? (
            <Section title={t('overview.activityTitle')}>
              <Strip
                items={[
                  { dt: t('overview.signups'), dd: number(funnel.data.data.signups) },
                  {
                    dt: t('overview.weeklyActive'),
                    dd: number(funnel.data.data.weekly_active_orgs),
                  },
                  {
                    dt: t('overview.startedOnFree'),
                    dd: number(funnel.data.data.started_on_free),
                  },
                  { dt: t('overview.fellToFree'), dd: number(funnel.data.data.fell_to_free) },
                ]}
              />
            </Section>
          ) : null}
        </>
      )}
      <FreeControls />
    </main>
  )
}

/** Pauses or resumes new organizations that would start on Free (D107). */
function FreeControls() {
  const { t, dateTime } = useI18n()
  const controls = useQuery(platformControlsQuery)
  const pause = useAdminMutation(setFreeOrgsPaused, [['platform', 'controls']])
  return (
    <Section title={t('overview.freeTitle')} meta={t('overview.freeSub')}>
      <div className="stack g1-free">
        <p className="g1-def">{t('overview.freeNote')}</p>
        {controls.isPending ? (
          <Loading />
        ) : controls.isError ? (
          <ErrorState error={controls.error} retry={() => controls.refetch()} />
        ) : (
          <>
            <p>
              {controls.data.free_orgs_paused
                ? t('overview.freePaused', {
                    reason: controls.data.free_orgs_paused_reason ?? '',
                    date: dateTime(controls.data.updated_at),
                  })
                : t('overview.freeOpen')}
            </p>
            <ActionForm
              label={t(
                controls.data.free_orgs_paused ? 'overview.freeResume' : 'overview.freePause',
              )}
              confirm={t(
                controls.data.free_orgs_paused
                  ? 'overview.freeResumeConfirm'
                  : 'overview.freePauseConfirm',
              )}
              onSubmit={(reason) =>
                pause.mutateAsync({ paused: !controls.data.free_orgs_paused, reason })
              }
            />
          </>
        )}
      </div>
    </Section>
  )
}
